import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ApolloError } from '@apollo/client';
import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import { getApplicationError } from '@oysterjs/core/graphql/errors';
import {
  ChevronRightIcon,
  PauseIcon,
  PencilSquareIcon,
  PlayIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { Spinner } from '@oysterjs/ui/spinner';
import {
  Role,
  SubscriptionEntitlement,
  type TriggerDefinitionFieldsFragment,
  TriggerDefinitionStatus,
  type TriggerEmailConfigFieldsFragment,
  TriggerEmailScopeMode,
  TriggerEventState,
  type TriggerEventListFieldsFragment,
  useGetTriggerDefinitionQuery,
  useGetTriggerEmailBuilderOptionsLazyQuery,
  useGetTriggerEventsQuery
} from '~/api/graphql';
import { emailConfigFromResponse } from '~/api/data/triggers/email-config';
import { emailRunFailureLabel } from '~/api/data/triggers/email-run-error';
import { useOptionalCurrentAgencyUser } from '~/components/current-user';
import { FormError } from '~/components/form-error';
import { useEntitlements } from '~/components/entitlements';
import { relativeDate } from '~/components/search/match';
import { DeleteTriggerDialog, useTriggerMutations } from './trigger-actions';
import {
  agencyEmailNamespace,
  eventHref,
  EventStateBadge,
  eventSummary,
  EventTypeBadge,
  RunStateBadge,
  triggerEditHref,
  TRIGGERS_PATH,
  TriggerStatusBadge
} from './trigger-utils';
import { TriggersProUpgradeDialog } from './triggers-upgrade';
import { useTriggersEntitlement } from './use-triggers-entitlement';
import { useRecentEmailRuns } from './use-recent-email-runs';
import {
  EmailIfStep,
  EmailWhenStep,
  WhenStep,
  namesPeople,
  type EmailDetailOptions,
  ThenStep
} from '~/components/triggers/trigger-flow-steps';

const RECENT_EVENT_COUNT = 10;

/** Mounted per trusted viewer/agency/definition/version; never reuse Apollo's address cache. */
const EmailConfigSteps = (props: {
  definitionId: string;
  config: TriggerEmailConfigFieldsFragment;
  canReadSetup: boolean;
}) => {
  const { definitionId, config, canReadSetup } = props;
  const needsOptions =
    (config.scope.mode === TriggerEmailScopeMode.Selected && config.scope.accounts.length > 0) ||
    namesPeople(emailConfigFromResponse(config).rule);
  const [loadOptions] = useGetTriggerEmailBuilderOptionsLazyQuery({
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
    context: { queryDeduplication: false }
  });
  const [options, setOptions] = React.useState<EmailDetailOptions>();
  const [loading, setLoading] = React.useState(canReadSetup && needsOptions);

  React.useEffect(() => {
    if (!canReadSetup || !needsOptions) return;
    let active = true;
    // The server selects the stored definition creator, never the editor
    // or a mailbox owner. Labels must not broaden account eligibility.
    void loadOptions({ variables: { definitionId } }).then(
      ({ data, error, errors }) => {
        if (!active) return;
        setOptions(error || errors?.length ? undefined : data?.triggerEmailBuilderOptions);
        setLoading(false);
      },
      () => {
        if (!active) return;
        setOptions(undefined);
        setLoading(false);
      }
    );
    return () => {
      active = false;
    };
  }, [canReadSetup, needsOptions, definitionId, loadOptions]);

  const draft = React.useMemo(() => emailConfigFromResponse(config), [config]);
  const visibleOptions = canReadSetup && needsOptions ? options : undefined;
  const mailboxLabel = (id: string) =>
    visibleOptions?.accounts.find((account) => account.id === id)?.address.trim() ||
    'Connected mailbox';
  return (
    <>
      <EmailWhenStep draft={draft} mailboxLabel={mailboxLabel} canReadSetup={canReadSetup} />
      <EmailIfStep draft={draft} options={visibleOptions} loading={loading && canReadSetup} />
    </>
  );
};

/** What this event meant for one trigger: its run, or the event's own state before or instead of matching. */
const TriggerOutcomeBadge = (props: {
  event: TriggerEventListFieldsFragment;
  triggerId: string;
}) => {
  const { event, triggerId } = props;
  if (event.state !== TriggerEventState.Evaluated) return <EventStateBadge state={event.state} />;
  const run = event.runs.find((r) => r.triggerDefinition.id === triggerId);
  return run ? <RunStateBadge state={run.state} /> : <Badge color="neutral">Not matched</Badge>;
};

const RecentActivityShell = (props: {
  loading: boolean;
  empty: boolean;
  children: React.ReactNode;
  notice?: React.ReactNode;
  fallback?: React.ReactNode;
}) => (
  <section>
    <h4 className="m-0 mb-3 text-sm font-semibold text-neutral-900 dark:text-white">
      Recent activity
    </h4>
    {props.notice}
    {props.loading ? (
      <div className="flex justify-center py-8">
        <Spinner size="size-6" color="neutral" />
      </div>
    ) : props.fallback ? (
      props.fallback
    ) : props.empty ? (
      <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-8 text-center dark:border-neutral-700 dark:bg-neutral-900/40">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          No events for this trigger yet.
        </p>
      </div>
    ) : (
      <div className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 dark:divide-neutral-700 dark:border-neutral-700">
        {props.children}
      </div>
    )}
  </section>
);

/**
 * A mailbox trigger's runs. Mailbox emails are not generic trigger events, so
 * they come from their own query: only runs whose workspace the viewer can open
 * (admins: every run), and never any mail content.
 */
export const RecentEmailRuns = ({ trigger }: { trigger: TriggerDefinitionFieldsFragment }) => {
  const user = useOptionalCurrentAgencyUser();
  // Remount before rendering a different principal's cached query result. Scope
  // includes locally known access changes, not just the account's stable ID.
  const scope = JSON.stringify([
    trigger.id,
    user?.id,
    user?.agency?.id,
    user?.state,
    user?.roles,
    user?.teams,
    user?.profile?.id,
    user?.permissions
  ]);
  return (
    <ScopedRecentEmailRuns
      key={scope}
      triggerId={trigger.id}
      enabled={!!user?.id && !!user?.agency?.id}
    />
  );
};

const ScopedRecentEmailRuns = ({ triggerId, enabled }: { triggerId: string; enabled: boolean }) => {
  const { runs, failed, pending, retry } = useRecentEmailRuns(triggerId, enabled);
  const hasRows = !!runs?.length;
  const feedback = failed ? (
    <div role="alert" className="mb-3 text-sm text-neutral-600 dark:text-neutral-300">
      <p className="m-0 mb-2">
        {hasRows
          ? 'Couldn’t refresh recent activity. Showing previously loaded activity.'
          : 'Couldn’t load recent activity.'}
      </p>
      <Button type="button" color="white" disabled={pending} onClick={retry}>
        {pending ? 'Retrying…' : 'Retry'}
      </Button>
    </div>
  ) : null;

  return (
    <RecentActivityShell
      loading={!failed && !runs}
      empty={runs?.length === 0}
      notice={hasRows ? feedback : null}
      fallback={!hasRows ? feedback : null}
    >
      {runs?.map((run) => {
        const body = (
          <>
            <div className="min-w-0 flex-1">
              <p className="m-0 truncate text-sm font-medium text-neutral-900 dark:text-white">
                {run.workspaceId ? 'Email matched · workspace created' : 'Email matched'}
              </p>
              <p className="m-0 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                {relativeDate(run.queuedAt)}
                {emailRunFailureLabel(run) ? ` · ${emailRunFailureLabel(run)}` : ''}
              </p>
            </div>
            <RunStateBadge state={run.state} />
          </>
        );
        return run.workspaceId ? (
          <Link
            key={run.id}
            to={`/app/workspaces/${run.workspaceId}`}
            className="flex items-center gap-x-3 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-white/5"
          >
            {body}
            <ChevronRightIcon className="size-5 shrink-0 text-neutral-400 dark:text-neutral-500" />
          </Link>
        ) : (
          <div key={run.id} className="flex items-center gap-x-3 px-4 py-3">
            {body}
          </div>
        );
      })}
    </RecentActivityShell>
  );
};

const RecentEvents = ({ trigger }: { trigger: TriggerDefinitionFieldsFragment }) =>
  trigger.eventMatcher.emailConfig ? (
    <RecentEmailRuns trigger={trigger} />
  ) : (
    <RecentGenericEvents trigger={trigger} />
  );

const RecentGenericEvents = ({ trigger }: { trigger: TriggerDefinitionFieldsFragment }) => {
  const { data, loading } = useGetTriggerEventsQuery({
    variables: { first: RECENT_EVENT_COUNT, triggerDefinitionId: trigger.id },
    pollInterval: 5000,
    fetchPolicy: 'cache-and-network'
  });
  const events = data?.triggerEvents.edges.map((edge) => edge.node) ?? [];

  return (
    <RecentActivityShell loading={loading && !data} empty={events.length === 0}>
      {events.map((event) => (
        <Link
          key={event.id}
          to={eventHref(event.id)}
          className="flex items-center gap-x-3 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-white/5"
        >
          <div className="min-w-0 flex-1">
            <p className="m-0 truncate text-sm font-medium text-neutral-900 dark:text-white">
              {eventSummary(event)}
            </p>
            <p className="m-0 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              {relativeDate(event.receivedAt)}
            </p>
          </div>
          <TriggerOutcomeBadge event={event} triggerId={trigger.id} />
          <ChevronRightIcon className="size-5 shrink-0 text-neutral-400 dark:text-neutral-500" />
        </Link>
      ))}
    </RecentActivityShell>
  );
};

const TriggerDetailContent = (props: {
  trigger: TriggerDefinitionFieldsFragment;
  emailNamespace: string;
  canManage: boolean;
  canReadSetup: boolean;
  setupScope: string;
  onUpgrade: () => void;
}) => {
  const navigate = useNavigate();
  const { trigger, emailNamespace, canManage, onUpgrade } = props;
  const { toggleStatus, pausing, resuming } = useTriggerMutations();
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [statusError, setStatusError] = React.useState<string | null>(null);

  /** Every action is offered to Basic users, who get the upgrade dialog instead. */
  const whenEntitled = (action: () => void) => () => (canManage ? action() : onUpgrade());

  const handleToggleStatus = whenEntitled(async () => {
    setStatusError(null);
    setStatusError(await toggleStatus(trigger));
  });

  const isMutating = pausing || resuming;
  const isPaused = trigger.status === TriggerDefinitionStatus.Paused;

  return (
    <div className="space-y-8 pb-8">
      <div>
        <h3 className="m-0 text-lg font-medium text-neutral-900 dark:text-white">{trigger.name}</h3>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <TriggerStatusBadge status={trigger.status} />
          <EventTypeBadge eventType={trigger.eventMatcher.eventType} />
        </div>
      </div>

      <ol className="m-0 list-none rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
        {trigger.eventMatcher.emailConfig ? (
          <EmailConfigSteps
            key={props.setupScope}
            definitionId={trigger.id}
            config={trigger.eventMatcher.emailConfig}
            canReadSetup={props.canReadSetup}
          />
        ) : (
          <WhenStep matcher={trigger.eventMatcher} emailNamespace={emailNamespace} />
        )}
        <ThenStep target={trigger.target} privacy={trigger.eventMatcher.emailConfig?.privacy} />
      </ol>
      <RecentEvents trigger={trigger} />

      <div className="border-t border-neutral-200 pt-6 dark:border-neutral-700">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            color="white"
            onClick={whenEntitled(() => navigate(triggerEditHref(trigger.id)))}
            disabled={isMutating}
          >
            <PencilSquareIcon className="size-4" />
            Edit
          </Button>
          <Button type="button" color="white" onClick={handleToggleStatus} loading={isMutating}>
            {isPaused ? <PlayIcon className="size-4" /> : <PauseIcon className="size-4" />}
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
          <Button
            type="button"
            color="white"
            onClick={whenEntitled(() => setShowDeleteDialog(true))}
            disabled={isMutating}
          >
            <TrashIcon className="size-4" />
            Delete
          </Button>
        </div>
        <FormError message={statusError} className="mt-3 text-sm text-red-600 dark:text-red-400" />
      </div>

      <DeleteTriggerDialog
        trigger={showDeleteDialog ? trigger : null}
        emailNamespace={emailNamespace}
        onClose={() => setShowDeleteDialog(false)}
        onDeleted={() => navigate(TRIGGERS_PATH, { replace: true })}
      />
    </div>
  );
};

/** Plain-language load failure; never relays the server's internal error text. */
export const triggerLoadErrorMessage = (error: ApolloError | Error): string => {
  const code =
    'graphQLErrors' in error ? getApplicationError(error as ApolloError)?.code : undefined;
  return code === ApplicationErrorCode.Unauthorized ||
    code === ApplicationErrorCode.Forbidden ||
    code === ApplicationErrorCode.NotFound
    ? 'This trigger was deleted, or you no longer have access to it.'
    : 'Could not load this trigger. Try again.';
};

export const TriggerDetailPage = () => {
  const navigate = useNavigate();
  const { triggerId } = useParams<{ triggerId: string }>();
  const agencyUser = useOptionalCurrentAgencyUser();
  const emailNamespace = agencyEmailNamespace(agencyUser?.agency);
  const canManageTriggers = useTriggersEntitlement();
  const { hasEntitlement } = useEntitlements();
  // A run-history audience is not a setup audience. The server still authorizes
  // every read; these guards prevent opportunistic privileged label requests.
  const canReadSetup = !!(
    agencyUser?.id &&
    agencyUser.agency?.id &&
    agencyUser.roles.includes(Role.Admin) &&
    hasEntitlement(SubscriptionEntitlement.Mailboxes) &&
    canManageTriggers
  );
  const [showProUpgradeDialog, setShowProUpgradeDialog] = React.useState(false);

  const { data, loading, error } = useGetTriggerDefinitionQuery({
    variables: { id: triggerId ?? '' },
    skip: !triggerId
  });

  const trigger = data?.triggerDefinition ?? null;

  const onClose = () => {
    navigate(TRIGGERS_PATH, { replace: true });
  };

  return (
    <>
      <MountableSlideout onClose={onClose} title="Trigger details">
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size="size-8" color="neutral" />
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-red-600 dark:text-red-400">
            {triggerLoadErrorMessage(error)}
          </div>
        ) : !trigger ? (
          <div className="py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
            Trigger not found.
          </div>
        ) : (
          <TriggerDetailContent
            trigger={trigger}
            emailNamespace={emailNamespace}
            canManage={canManageTriggers}
            canReadSetup={canReadSetup && trigger.id === triggerId}
            setupScope={JSON.stringify([
              agencyUser?.agency?.id,
              agencyUser?.id,
              triggerId,
              trigger.id,
              trigger.version,
              canReadSetup
            ])}
            onUpgrade={() => setShowProUpgradeDialog(true)}
          />
        )}
      </MountableSlideout>
      <TriggersProUpgradeDialog
        open={showProUpgradeDialog}
        onClose={() => setShowProUpgradeDialog(false)}
      />
    </>
  );
};
